import { z } from 'zod';

/**
 * Zod schema for an online appraisal request. Used both for per-step
 * client-side validation and for validating the submission in the route action.
 *
 * The wizard serializes the whole payload to a single JSON string posted under
 * the `payload` form field (front-end first, mock back-end — see the TODO in
 * app/routes/appraisal.tsx for the planned Stack9 hook-up).
 */

const requiredString = (message: string) => z.string({ required_error: message }).trim().min(1, message);

export const propertySchema = z.object({
  address: requiredString('Enter the property address.'),
  propertyType: requiredString('Select a property type.'),
  bedrooms: z.number({ required_error: 'Enter the number of bedrooms.' }).int().min(0),
  bathrooms: z.number({ required_error: 'Enter the number of bathrooms.' }).int().min(0),
  carSpaces: z.number({ required_error: 'Enter the number of car spaces.' }).int().min(0),
  landSizeSqm: z.number().positive('Land size must be greater than zero.').optional(),
});

export const timelineSchema = z.object({
  reason: requiredString('Select a reason for this appraisal.'),
  appraisalType: z.enum(['in-person', 'desktop'], { required_error: 'Choose an appraisal type.' }),
  timeframe: requiredString('Select an ideal timeframe.'),
});

export const photoSchema = z.object({
  name: z.string(),
  size: z.number(),
  type: z.string(),
});

export const contactSchema = z.object({
  firstName: requiredString('Enter your first name.'),
  lastName: requiredString('Enter your last name.'),
  email: requiredString('Enter your email.').email('Enter a valid email address.'),
  phone: requiredString('Enter your phone number.'),
  preferredMethod: requiredString('Select a preferred contact method.'),
  preferredTime: requiredString('Select a preferred time to be contacted.'),
  consent: z.literal(true, { message: 'You must agree to be contacted to continue.' }),
});

export const appraisalPayloadSchema = z.object({
  property: propertySchema,
  timeline: timelineSchema,
  notes: z.string().trim().optional(),
  photos: z.array(photoSchema).optional(),
  contact: contactSchema,
});

export type Property = z.infer<typeof propertySchema>;
export type Timeline = z.infer<typeof timelineSchema>;
export type Photo = z.infer<typeof photoSchema>;
export type Contact = z.infer<typeof contactSchema>;
export type AppraisalPayload = z.infer<typeof appraisalPayloadSchema>;

/**
 * Loose schema describing the wizard draft, where every numeric field is still
 * the raw string an <input> holds and fields may be empty while the user fills
 * the form (validation with `appraisalPayloadSchema` runs before advancing or
 * submitting). This schema also validates persisted browser data on load — if a
 * stored draft no longer matches this shape (e.g. after a field changes), it is
 * discarded rather than crashing the wizard.
 */
export const appraisalDraftSchema = z.object({
  property: z.object({
    address: z.string(),
    propertyType: z.string(),
    bedrooms: z.string(),
    bathrooms: z.string(),
    carSpaces: z.string(),
    landSizeSqm: z.string(),
  }),
  timeline: z.object({
    reason: z.string(),
    appraisalType: z.union([z.literal('in-person'), z.literal('desktop'), z.literal('')]),
    timeframe: z.string(),
  }),
  notes: z.string(),
  photos: z.array(photoSchema),
  contact: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phone: z.string(),
    preferredMethod: z.string(),
    preferredTime: z.string(),
    consent: z.boolean(),
  }),
});

export type AppraisalDraft = z.infer<typeof appraisalDraftSchema>;

export function createEmptyDraft(): AppraisalDraft {
  return {
    property: {
      address: '',
      propertyType: '',
      bedrooms: '',
      bathrooms: '',
      carSpaces: '',
      landSizeSqm: '',
    },
    timeline: {
      reason: '',
      appraisalType: '',
      timeframe: '',
    },
    notes: '',
    photos: [],
    contact: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      preferredMethod: '',
      preferredTime: '',
      consent: false,
    },
  };
}

/** Converts a draft (string-based inputs) into the shape validated by Zod. */
export function draftToPayload(draft: AppraisalDraft) {
  return {
    property: {
      address: draft.property.address,
      propertyType: draft.property.propertyType,
      bedrooms: draft.property.bedrooms === '' ? undefined : Number(draft.property.bedrooms),
      bathrooms: draft.property.bathrooms === '' ? undefined : Number(draft.property.bathrooms),
      carSpaces: draft.property.carSpaces === '' ? undefined : Number(draft.property.carSpaces),
      landSizeSqm: draft.property.landSizeSqm === '' ? undefined : Number(draft.property.landSizeSqm),
    },
    timeline: {
      reason: draft.timeline.reason,
      appraisalType: draft.timeline.appraisalType || undefined,
      timeframe: draft.timeline.timeframe,
    },
    notes: draft.notes.trim() || undefined,
    photos: draft.photos,
    contact: {
      firstName: draft.contact.firstName,
      lastName: draft.contact.lastName,
      email: draft.contact.email,
      phone: draft.contact.phone,
      preferredMethod: draft.contact.preferredMethod,
      preferredTime: draft.contact.preferredTime,
      consent: draft.contact.consent || undefined,
    },
  };
}
